import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { Dropdown, Tooltip } from 'antd'
import type { MenuProps } from 'antd'
import type { MenuItemType } from 'antd/es/menu/interface'
import { Brush, FileText, ChevronDown } from 'lucide-react'
import type { DockAction } from '../BaseNodeTypes'
import { NodeInteractionZone } from '../NodeInteractionZone'
import {
  FooterBarContainer,
  FooterButtonRow,
  FooterButton,
  SourceList,
  SourceCard,
  SourceHeader,
  SourceFileName,
  SourceExcerpt,
} from '../styles/CardFooterBarStyles'

interface RagSource {
  fileName: string
  excerpt: string
}

interface CardFooterBarProps {
  prototypeActions?: DockAction[]
  ragSources?: RagSource[]
}

const SOURCE_ICON_COLOR = '#3b82f6'
const SOURCE_ICON_OPACITY = 0.7
const CHEVRON_SIZE = 12

/**
 * Slim footer bar at the bottom of node cards.
 * Left side: Prototype dropdown (IdeaNode only).
 * Right side: Sources accordion toggle (any node with ragSources).
 */
export const CardFooterBar = React.memo(
  ({ prototypeActions, ragSources }: CardFooterBarProps): React.ReactElement | null => {
    const [sourcesOpen, setSourcesOpen] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)

    const hasPrototype = prototypeActions !== undefined && prototypeActions.length > 0
    const hasSources = ragSources !== undefined && ragSources.length > 0

    const toggleSources = useCallback(() => {
      setSourcesOpen((prev) => !prev)
    }, [])

    // Close sources accordion when clicking outside the node card
    useEffect(() => {
      if (!sourcesOpen) return undefined
      const handleClickOutside = (e: PointerEvent): void => {
        // Walk up to find the node card container
        const nodeCard = containerRef.current?.closest('[data-node-id]')
        if (nodeCard && !nodeCard.contains(e.target as Node)) {
          setSourcesOpen(false)
        }
      }
      document.addEventListener('pointerdown', handleClickOutside, true)
      return () => document.removeEventListener('pointerdown', handleClickOutside, true)
    }, [sourcesOpen])

    const prototypeMenuItems: MenuProps['items'] = useMemo(() => {
      if (!prototypeActions) return []
      return prototypeActions.map((action) => {
        const ActionIcon = action.icon
        const label = <span>{action.label}</span>

        return {
          key: action.id,
          icon: <ActionIcon size={14} />,
          label:
            action.disabled && action.disabledReason ? (
              <Tooltip title={action.disabledReason} placement="right">
                {label}
              </Tooltip>
            ) : (
              label
            ),
          disabled: action.disabled,
          onClick: ({ domEvent }: { domEvent: React.MouseEvent }) => {
            domEvent.stopPropagation()
            action.onClick(domEvent)
          },
        } as MenuItemType
      })
    }, [prototypeActions])

    if (!hasPrototype && !hasSources) return null

    return (
      <NodeInteractionZone>
        <FooterBarContainer ref={containerRef}>
          {/* Button row — stays at top */}
          <FooterButtonRow>
            {hasPrototype && (
              <Dropdown
                menu={{ items: prototypeMenuItems }}
                trigger={['click']}
                placement="bottomLeft"
                getPopupContainer={() => document.body}
              >
                <FooterButton onClick={(e) => e.stopPropagation()}>
                  <Brush size={12} style={{ opacity: 0.6 }} />
                  Prototype
                </FooterButton>
              </Dropdown>
            )}

            {hasSources && (
              <FooterButton onClick={toggleSources} style={{ marginLeft: 'auto' }}>
                <FileText size={10} style={{ opacity: 0.6 }} />
                {ragSources.length} {ragSources.length === 1 ? 'SOURCE' : 'SOURCES'}
                <ChevronDown
                  size={CHEVRON_SIZE}
                  style={{
                    transition: 'transform 0.2s',
                    transform: sourcesOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              </FooterButton>
            )}
          </FooterButtonRow>

          {/* Expanded sources list — below the button row */}
          {sourcesOpen && hasSources && (
            <SourceList>
              {ragSources.map((source, i) => (
                <SourceCard key={i}>
                  <SourceHeader>
                    <FileText
                      size={11}
                      style={{
                        color: SOURCE_ICON_COLOR,
                        flexShrink: 0,
                        opacity: SOURCE_ICON_OPACITY,
                      }}
                    />
                    <SourceFileName>{source.fileName}</SourceFileName>
                  </SourceHeader>
                  <SourceExcerpt>&ldquo;{source.excerpt}&rdquo;</SourceExcerpt>
                </SourceCard>
              ))}
            </SourceList>
          )}
        </FooterBarContainer>
      </NodeInteractionZone>
    )
  }
)

CardFooterBar.displayName = 'CardFooterBar'
